<template>
  <NotFound v-if="residencyNotFound" />
  <div v-else-if="residencyBySlug" class="px-4 py-12 md:py-16 mx-auto max-w-7xl">
    <p class="text-fg-disclaimer">Residency at {{ venue_name }}</p>
    <h1 class="text-4xl font-semibold font-heading">
      {{ residencyBySlug.name }}
    </h1>
    <p id="residency-dates" class="text-secondary text-lg">
      <span>{{ settingStore.fullDate(residencyBySlug.start_date) }}</span>
      <span v-if="showEndDate"> - {{ settingStore.fullDate(residencyBySlug.end_date) }}</span>
    </p>

    <div class="mt-6 flex flex-col-reverse gap-8 lg:flex-row">
      <div v-if="show" class="flex flex-col gap-2 lg:w-1/2">
        <!--SHOW IMAGE-->
        <div class="w-full">
          <img
            id="residency-image"
            :src="show.srcset.rectXl.src"
            :srcset="buildShowSrcset(show.srcset)"
            sizes="(min-width: 1280px) 624px, (min-width: 1024px) calc(50vw - 1rem), calc(100vw - 2rem)"
            :alt="show.name"
            class="w-full h-auto"
          />
        </div>

        <!--SHOW DESCRIPTION-->
        <div
          id="residency-description"
          class="whitespace-pre-wrap"
          v-html="sanitizedResidencyDescription"
        />

        <!--YOUTUBE VIDEO URL-->
        <div v-if="youtubeEmbedSrc" id="residency-video" class="aspect-video w-full">
          <iframe
            :src="youtubeEmbedSrc"
            title="Residency video"
            class="w-full h-full"
            loading="lazy"
            referrerpolicy="strict-origin-when-cross-origin"
            sandbox="allow-scripts allow-same-origin allow-presentation allow-popups allow-popups-to-escape-sandbox"
            :allow="YOUTUBE_IFRAME_ALLOW"
            allowfullscreen
          ></iframe>
        </div>
      </div>
      <div class="flex flex-col gap-4 lg:grow">
        <!--PERFORMANCE LIST-->
        <div>
          <div
            class="pb-4 border-b border-linear-g-primary text-lg flex justify-between gap-2 items-center font-semibold font-heading"
          >
            <h2>Shows</h2>
            <h2>Buy Tickets</h2>
          </div>
          <template v-if="residencyShows.length > 0">
            <div
              v-for="show in residencyShows"
              :key="show.id"
              :id="`show-${show.id}-date-${show.date}`"
              class="py-4 border-b border-linear-g-primary flex flex-col gap-1 items-start justify-start"
            >
              <div class="show-header">
                <p class="text-sm line-clamp-1">{{ show.name }}</p>
                <p>{{ settingStore.mediumDate(show.datetime) }}</p>
              </div>
              <div class="flex gap-2 justify-end items-start flex-wrap w-full">
                <PerformanceButton
                  v-for="performance in show.performances"
                  :key="performance.id"
                  :performance="performance"
                  :error="error"
                  :id="`performance-${performance.id}`"
                  :forcePurchase="
                    Boolean(inviteCode) ||
                    checkoutStore.isPerformanceUnlockedByInviteCode(performance)
                  "
                />
              </div>
            </div>
          </template>
          <template v-else>
            <p>No upcoming shows</p>
          </template>
        </div>

        <!--SHARE-->
        <ShareButton
          id="residency-share-button"
          :url="residencyPublicLink"
          :title="residencyBySlug.name"
          :text="`${residencyBySlug.name} at ${venue_name}`"
        />

        <!--EXTERNAL LINKS-->
        <div v-if="sortedExternalLinks.length > 0" id="residency-external-links">
          <ul class="flex flex-col gap-2">
            <li
              v-for="link in sortedExternalLinks"
              :key="link.id ?? `${link.position}-${link.url}`"
            >
              <a
                :id="`residency-external-link-${link.id ?? link.position}`"
                :href="safeHref(link.url)"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-2 text-secondary hover:opacity-80"
              >
                <component :is="iconComponentByName[iconFor(link)]" class="w-5 h-5" />
                <span>{{ link.label }}</span>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="container px-4 py-12 md:py-16 mx-auto">
    <p>Loading Residency...</p>
  </div>
</template>

<script lang="ts" setup>
import { isEmpty } from 'lodash-es'
import { storeToRefs } from 'pinia'
import {
  computed,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  onServerPrefetch,
  ref,
  toRefs,
} from 'vue'
import { useRoute } from 'vue-router'

import { usePostHog } from '@/common/composables/usePostHog'
import { buildShowSrcset } from '@/models'
import ShareButton from '@/modules/guestUi/components/molecules/ShareButton.vue'
import PerformanceButton from '@/modules/guestUi/components/organisms/PerformanceButton.vue'
import { DisplayShow } from '@/modules/guestUi/interfaces/shows'
import NotFound from '@/modules/guestUi/pages/NotFound.vue'
import {
  DEFAULT_RESIDENCY_LINK_ICON,
  ResidencyLinkIconName,
  iconComponentByName,
  isResidencyLinkIconName,
} from '@/modules/residency/constants/ResidencyExternalLinkIcons'
import { upcomingPerformances } from '@/modules/residency/utils/performances'
import {
  buildResidencyCanonicalPath,
  buildResidencyJsonLd,
  buildResidencyPath,
  buildResidencyUrl,
  shareImage,
  shareImageDimensionsPx,
} from '@/modules/residency/utils/seo'
import { getYouTubeEmbedUrl, getYouTubeThumbnailUrl } from '@/modules/residency/utils/youtube'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'
import { ErrorData, blankError } from '@/utils/errors'
import { buildMetaDescription } from '@/utils/metaDescription'
import { buildPageTitle } from '@/utils/pageTitle'
import { sanitizeHtmlForClient } from '@/utils/sanitize'
import { sortBy } from '@/utils/utils'

import { ResidencyExternalLinkRead, ResidencyRead, ShowRead } from '@generated/types'

const error = ref<ErrorData>(blankError())
const checkoutStore = useCheckoutStore()
const settingStore = useSettingStore()
const { venue_name } = storeToRefs(settingStore)

const props = defineProps<{ slug: string }>()
const { slug } = toRefs(props)
const residencyBySlug = ref<ResidencyRead | null>(null)
const residencyNotFound = ref(false)
let isUnmounted = false

const route = useRoute()
const inviteCode = computed<string | undefined>(() => route.query.invite as string | undefined)
onMounted(async () => {
  await checkoutStore.prepareCheckout(inviteCode.value)
})

async function load() {
  // ssr-render.js turns this flag into an HTTP 404, so it can't be left wherever the previous
  // load put it.
  settingStore.notFound = false
  try {
    try {
      residencyNotFound.value = false
      residencyBySlug.value = await checkoutStore.getResidency(slug.value)
      if (!residencyBySlug.value || isEmpty(residencyBySlug.value)) {
        residencyNotFound.value = true
      }
    } catch (e) {
      residencyNotFound.value = true
    }
    if (residencyNotFound.value) {
      // Clear every field the found path sets. Inert while SSR builds a store per request, but
      // load() also runs client-side, and a half-reset branch is one shared store away from
      // serving another residency's description.
      settingStore.ssrTitle = settingStore.venue_name
      settingStore.ssrCanonicalPath = null
      settingStore.ssrDescription = null
      settingStore.ssrImage = null
      settingStore.ssrImageDimensionsPx = null
      settingStore.ssrVideoUrl = null
      settingStore.ssrJsonLd = null
      settingStore.notFound = true
      return
    }
    const residency = residencyBySlug.value
    settingStore.ssrTitle = buildPageTitle([residency?.name], settingStore, { locality: true })
    settingStore.ssrCanonicalPath = residency?.slug
      ? buildResidencyCanonicalPath(residency, route.path)
      : null
    settingStore.ssrDescription = buildMetaDescription(
      residency?.description || residency?.show?.description || '',
    )
    const videoUrl = residencyBySlug.value?.video_url || ''
    const galleryImage = shareImage(residencyBySlug.value?.show)
    settingStore.ssrImage = galleryImage || getYouTubeThumbnailUrl(videoUrl) || ''
    settingStore.ssrImageDimensionsPx = shareImageDimensionsPx(residencyBySlug.value?.show)
    settingStore.ssrVideoUrl = videoUrl ? getYouTubeEmbedUrl(videoUrl) : null
    settingStore.ssrJsonLd = buildResidencyJsonLd(
      residency,
      settingStore,
      buildResidencyUrl(settingStore.canonicalDomain, residency?.slug, route.path),
    )
    if (typeof document !== 'undefined' && !isUnmounted) {
      usePostHog().registerPageVersionForSession('residency_page_version', 'hub')
    }
  } finally {
    settingStore.ssrPrefetchDone = true
  }
}

onServerPrefetch(load)
onBeforeMount(load)
onBeforeUnmount(() => (isUnmounted = true))

const residencyPublicLink = computed<string>(function () {
  if (!residencyBySlug.value) return ''
  const path = buildResidencyPath(residencyBySlug.value.slug, route.path)
  return `https://${settingStore.siteDomain}${path}`
})
const show = computed<ShowRead | undefined>(() => residencyBySlug.value?.show)
const showEndDate = computed<boolean>(
  () =>
    !!residencyBySlug.value?.end_date &&
    residencyBySlug.value.end_date !== residencyBySlug.value.start_date,
)
const sanitizedResidencyDescription = computed(() =>
  sanitizeHtmlForClient(residencyBySlug.value?.description || show.value?.description),
)

const youtubeEmbedSrc = computed<string | null>(() =>
  getYouTubeEmbedUrl(residencyBySlug.value?.video_url ?? ''),
)

const sortedExternalLinks = computed<ResidencyExternalLinkRead[]>(() =>
  [...(residencyBySlug.value?.external_links ?? [])].sort((a, b) => a.position - b.position),
)

function iconFor(link: ResidencyExternalLinkRead): ResidencyLinkIconName {
  return isResidencyLinkIconName(link.icon) ? link.icon : DEFAULT_RESIDENCY_LINK_ICON
}

function safeHref(url: string): string {
  return /^https?:\/\//i.test(url) ? url : '#'
}

const YOUTUBE_IFRAME_ALLOW =
  'accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share'

const residencyShows = computed<DisplayShow[]>(function () {
  if (!residencyBySlug.value) return []

  const shows: DisplayShow[] = []
  for (const performance of upcomingPerformances(
    residencyBySlug.value.performances,
    settingStore.timezone,
  )) {
    const performanceDate = settingStore.dateIsoStr(performance.datetime)
    const show = shows.find(show => show.date === performanceDate && show.id == performance.show_id)

    if (show) {
      show.performances.push(performance)
      sortBy(show.performances, 'datetime')
    } else {
      const desc = performance.show?.description || ''
      shows.push({
        ...performance.show,
        id: performance.show_id,
        date: performanceDate,
        datetime: performance.datetime,
        dateStr: settingStore.mediumDate(performance.datetime),
        description: desc,
        position: performance.show?.position || [],
        performances: [performance],
      })
    }
  }

  return sortBy(shows, 'datetime')
})
</script>
